import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Hook pour gérer la mise en surbrillance des boutons depuis la checklist
 *
 * @param targetId - L'identifiant du bouton à surveiller (ex: 'create-team')
 * @param duration - Durée de l'effet en ms (défaut: 5000ms = 5 secondes)
 * @returns boolean - true si le bouton doit être en surbrillance
 *
 * Usage:
 * const isHighlighted = useHighlightButton('create-team');
 * <Button className={isHighlighted ? 'animate-highlight-pulse' : ''}>
 */
export function useHighlightButton(targetId: string, duration: number = 20000): boolean {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isHighlighted, setIsHighlighted] = useState(false);
  const hasTriggered = useRef(false);

  useEffect(() => {
    const highlightParam = searchParams.get('highlight');

    // Vérifier si ce bouton doit être mis en surbrillance via URL param
    const shouldHighlight = highlightParam === targetId;

    if (shouldHighlight && !hasTriggered.current) {
      hasTriggered.current = true;
      setIsHighlighted(true);

      // Retirer le paramètre de l'URL si présent
      if (highlightParam) {
        const cleanupTimeout = setTimeout(() => {
          setSearchParams((prev) => {
            const newParams = new URLSearchParams(prev);
            newParams.delete('highlight');
            return newParams;
          }, { replace: true });
        }, 100);
      }

      // Arrêter l'effet après la durée spécifiée
      const highlightTimeout = setTimeout(() => {
        setIsHighlighted(false);
      }, duration);

      return () => {
        clearTimeout(highlightTimeout);
      };
    }
  }, [searchParams, targetId, duration, setSearchParams]);

  return isHighlighted;
}
